import { http, notify } from 'utils'
import Vue from 'vue'
import {concat, findIndex, pick, set} from 'lodash'
import moment from 'moment'

import {
  SET_THREAD, SET_THREADS, SET_THREAD_PROPERTY, SET_THREADS_THREAD, ADD_THREADS, SET_THREADS_PAGINATION,
  ADD_MESSAGE, SET_MESSAGE, SET_MESSAGE_BY_ID, SET_UNREAD_COUNT, REMOVE_MESSAGE, INCREMENT_UNREAD_COUNT,
  SET_THREAD_FILTER, RESET_THREAD_FILTERS, SET_THREAD_PARTICIPANT_READ, SET_TYPING, SET_SENDING,
  CLEAR_THREAD
} from 'vx/mutations'

import initialFiltersState from 'vx/state/message/filters'

// Message state.
const state = {
  filters: initialFiltersState(),
  messages: [],
  thread: { booking: {id: 0} },
  pagination: {},
  threads: [],
  typers: [],
  unread_count: 0,
  sending: false
}

let clearTimerIds = {}
// Message state getters.
const getters = {
  /**
   * Initial booking filters state.
   *
   * @return object
   */
  initialFiltersState () {
    return initialFiltersState()
  },

  /**
   * Determine if we have no more threads to load.
   *
   * @return bool
   */
  haveNoMoreThreads: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

const actions = {
  /**
   * Send a message
   *
   * @return Promise
   */
  sendMessage ({ commit }, { threadId, text }) {
    commit(SET_SENDING, true)
    return http.post(`message/${threadId}`, { 'text': text })
      .then((response) => {
        commit(SET_SENDING, false)
        return response
      })
  },

  /**
   * Upload a File
   *
   * @return Promise
   */
  uploadFile ({ commit }, { threadId, file }) {
    let payload = new FormData()
    payload.append('file', file)

    commit(SET_SENDING, true)

    return http.post(`message/${threadId}/upload_file`, payload, {
      headers: {'Content-Type': 'multipart/form-data'},
      maxContentLength: 15000000
    })
      .then((response) => {
        commit(SET_SENDING, false)
        return response
      })
  },

  updateParticipantsUnreads ({ commit }, { messageId, participantId, threadId }) {
    commit(SET_THREAD_PARTICIPANT_READ, { messageId, participantId, threadId })
  },

  /**
   * Get threads.
   *
   * @return Promise
   */
  getUnreadCount ({ commit }, params = {}) {
    return http.get('threads/unread_count', { params })
      .then((response) => {
        commit(SET_UNREAD_COUNT, response.data)
      })
  },

  /**
   * Get threads.
   *
   * @return Promise
   */
  getThreads ({ commit }, params = {}) {
    return http.get('threads', { params })
      .then((response) => {
        commit(SET_THREADS, response.data.data)
        commit(SET_THREADS_PAGINATION, response.data.meta)
      })
  },

  /**
   * Get next set of paginated threads.
   *
   * @return Promise
   */
  getNextThreads ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreThreads) {
      return Promise.reject(new Error('There are no more threads to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`threads`, { params })
      .then((response) => {
        commit(ADD_THREADS, response.data.data)
        commit(SET_THREADS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Get threads by id.
   *
   * @return Promise
   */
  getThread ({commit, dispatch}, {threadId, userId, tenantSlug, getFiles = false}) {
    return http.get(`thread/${threadId}`)
      .then((response) => {
        Vue.prototype.$echo.private(tenantSlug + '.thread.' + threadId)
          .listenForWhisper('UserTyping', (payload) => {
            commit(SET_TYPING, {payload: payload, value: true})
            clearTimeout(clearTimerIds[payload.id])
            clearTimerIds[payload.id] = setTimeout(() => {
              commit(SET_TYPING, {payload: payload, value: false})
            }, 2000)
          })
          .listenForWhisper('MessageRead', (payload) => {
            commit(SET_THREAD_PARTICIPANT_READ, {
              messageId: payload.messageId,
              participantId: payload.participantId,
              threadId: payload.threadId
            })
          })
          .listen('BookingFileUploaded', (payload) => {
            dispatch('booking/file/addFile', {file: payload.file}, {root: true})
          })
        commit(SET_THREAD, response.data)
        if (getFiles) {
          dispatch('booking/file/getBookingFiles', {
            id: response.data.booking.id,
            threadId: response.data.id
          }, {root: true})
        }
        dispatch('markAllMessagesRead', {
          threadId: response.data.id,
          messageId: response.data.latest_message ? response.data.latest_message.id : null,
          userId,
          count: response.data.unread_count,
          tenantSlug
        })
        this._vm.$analytics.track({
          eventName: 'Opportunity Chat Open',
          opportunityId: response.data.booking.id,
          opportunityStatus: response.data.booking.status
        })
      })
      .catch(() => {
        notify.error('Unable to find chat')
      })
  },

  /**
   * Get threads by id.
   *
   * @return Promise
   */
  acceptTerms ({ commit }, { threadId }) {
    return http.put(`thread/accept_terms/${threadId}`)
      .then((response) => {
        commit(SET_THREAD, response.data)
      })
  },

  /**
   * Clear the thread in state
   *
   * @param commit
   */
  clearThread ({ commit }, tenantSlug) {
    let threadId = state.thread.id
    const targetElement = document.getElementById('mainbody')
    targetElement.style.removeProperty('overflow')
    commit(CLEAR_THREAD)
    Vue.prototype.$echo.leave(tenantSlug + '.thread.' + threadId)
  },

  /**
   * Reopen a thread
   *
   * @param commit
   * @param threadId
   * @returns {*}
   */
  reopenThread ({ commit }, { threadId }) {
    return http.post(`thread/reopen/${threadId}`)
      .then(() => {
        commit(SET_THREAD_PROPERTY, { property: 'active', value: true, threadId: threadId })
      })
  },

  /**
   * Close a thread
   *
   * @param commit
   * @param threadId
   * @returns {*}
   */
  closeThread ({ commit }, { threadId }) {
    return http.post(`thread/close/${threadId}`)
      .then(() => {
        commit(SET_THREAD_PROPERTY, { property: 'active', value: false, threadId: threadId })
      })
  },

  /**
   * Pin a thread
   *
   * @param commit
   * @param threadId
   * @returns {*}
   */
  pinThread ({ commit }, { threadId }) {
    return http.post(`thread/pin/${threadId}`)
      .then(() => {
        commit(SET_THREAD_PROPERTY, { property: 'pinned', value: true, threadId: threadId })
      })
  },

  /**
   * Unpin a thread
   *
   * @param commit
   * @param threadId
   * @returns {*}
   */
  unpinThread ({ commit }, { threadId }) {
    return http.post(`thread/unpin/${threadId}`)
      .then(() => {
        commit(SET_THREAD_PROPERTY, { property: 'pinned', value: false, threadId: threadId })
      })
  },

  /**
   * Mark all messages as read for a user
   *
   * @param commit
   * @param threadId
   * @param count
   * @returns {Promise<any>}
   */
  markAllMessagesRead ({ commit }, { threadId, messageId, userId, count, tenantSlug }) {
    Vue.prototype.$echo.private(tenantSlug + '.thread.' + threadId)
      .whisper('MessageRead', {
        participantId: userId,
        messageId: messageId,
        threadId: threadId
      })
    return http.put(`read_messages/${threadId}`)
      .then((response) => {
        if (count) { count = 1 }
        let varCount = state.unread_count - count
        if (varCount < 0) {
          varCount = 0
        }
        commit(SET_THREAD_PROPERTY, { property: 'unread_count', value: 0, threadId: threadId })
        commit(SET_UNREAD_COUNT, varCount)
      })
  },

  /**
   * Add a message to a thread
   *
   * @param commit
   * @param payload
   */
  addMessage ({ commit }, payload) {
    commit(ADD_MESSAGE, payload)
  },

  /**
   * Increment the unread count for a thread
   *
   * @param commit
   * @param payload
   */
  incrementUnreadCount ({ commit }, payload) {
    commit(INCREMENT_UNREAD_COUNT, payload)
  },

  /**
   * Remove a message from a thread
   *
   * @param commit
   * @param id
   */
  removeMessage ({ commit }, id) {
    commit(REMOVE_MESSAGE, id)
    commit(SET_SENDING, false)
  },

  /**
   * Update the details of a message on a thread
   *
   * @param commit
   * @param payload
   * @param tempMessageId
   */
  updateMessage ({ commit }, { payload, tempMessageId }) {
    commit(SET_MESSAGE_BY_ID, {payload: payload, id: tempMessageId})
  },

  /**
   * Delete a message from a thread
   *
   * @param commit
   * @param threadId
   * @param messageId
   * @returns {*}
   */
  deleteMessage ({ commit }, { threadId, messageId }) {
    return http.post(`message/delete`, { 'thread_id': threadId, 'message_id': messageId })
      .then((response) => {
        commit(SET_MESSAGE, response.data)
      })
  },

  /**
   * Restore a deleted message on a thread
   *
   * @param commit
   * @param threadId
   * @param messageId
   * @returns {*}
   */
  restoreMessage ({ commit }, { threadId, messageId }) {
    return http.post(`message/restore`, { 'thread_id': threadId, 'message_id': messageId })
      .then((response) => {
        commit(SET_MESSAGE, response.data)
      })
  },

  removeFileFromMessages ({ commit }, payload) {
    commit('REMOVE_FILE', payload)
  },

  setFilter ({ commit }, { filter, value }) {
    commit(SET_THREAD_FILTER, { filter, value })
  },

  resetFilters ({ commit }) {
    commit(RESET_THREAD_FILTERS)
  }
}

// Message state mutations.
const mutations = {
  [SET_TYPING] (state, { payload, value }) {
    if (value) {
      let index = state.typers.findIndex(current => current.id === payload.id)
      if (index === -1) {
        state.typers.push(payload)
      }
      state.thread.typing = state.typers.length
    } else {
      let index = state.typers.findIndex(current => current.id === payload.id)
      if (index !== -1) {
        state.typers.splice(index, 1)
        state.thread.typing = state.typers.length
      }
    }
  },

  [SET_SENDING] (state, sending) {
    state.sending = sending
  },

  [SET_UNREAD_COUNT] (state, count) {
    state.unread_count = count < 0 ? 0 : count
  },

  [SET_THREAD] (state, thread) {
    state.thread = thread
  },

  [CLEAR_THREAD] (state) {
    state.thread = { booking: {id: 0} }
  },

  [SET_THREADS] (state, threads) {
    state.threads = threads
  },

  [SET_THREAD_PROPERTY] (state, { property, value, threadId }) {
    if (state.thread && threadId === state.thread.id) {
      set(state.thread, property, value)
    }
    let index = findIndex(state.threads, current => current.id === threadId)

    if (index !== -1) {
      set(state.threads[index], property, value)
    }
  },

  [SET_THREAD_PARTICIPANT_READ] (state, { messageId, participantId, threadId }) {
    if (state.thread && threadId === state.thread.id) {
      let index = findIndex(state.thread.participants, current => current.id === participantId)
      if (index !== -1) {
        set(state.thread.participants[index], 'message_id', messageId)
      }
    }
  },

  [SET_THREADS_THREAD] (state, thread) {
    let index = findIndex(state.threads, current => current.id === thread.id)

    if (index !== -1) {
      state.threads.splice(index, 1, thread)
    }
  },

  [SET_THREADS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_THREADS] (state, threads) {
    state.threads = concat(state.threads, threads)
  },

  [REMOVE_MESSAGE] (state, id) {
    if (state.thread) {
      let index = findIndex(state.thread.messages, current => current.id === id)
      if (index !== -1) {
        state.thread.messages.splice(index, 1)
      }
    }
  },

  [ADD_MESSAGE] (state, payload) {
    if (state.thread && payload.message.thread_id === state.thread.id) {
      state.thread.messages.push(payload.message)
    }
    let index = findIndex(state.threads, current => current.id === payload.message.thread_id)
    if (index !== -1) {
      state.threads[index].latest_message = payload.message
    }
  },

  [INCREMENT_UNREAD_COUNT] (state, payload) {
    if (state.thread && payload.message.thread_id === state.thread.id) {
      state.thread.unread_count = state.thread.unread_count + 1
    } else {
      let index = findIndex(state.threads, current => current.id === payload.message.thread_id)
      if (index !== -1) {
        if (state.threads[index].unread_count === 0) {
          state.unread_count = state.unread_count + 1
        }
        state.threads[index].unread_count = state.threads[index].unread_count + 1
      }
    }
  },

  [SET_MESSAGE] (state, message) {
    let index = findIndex(state.thread.messages, current => current.id === message.id)

    if (index !== -1) {
      state.thread.messages.splice(index, 1, message)
    }
  },

  [SET_MESSAGE_BY_ID] (state, { payload, id }) {
    let index = findIndex(state.thread.messages, current => current.id === id)

    if (index !== -1) {
      state.thread.messages.splice(index, 1, payload)
    }
  },

  [SET_THREAD_FILTER] (state, { filter, value }) {
    set(state.filters, filter, value)
  },

  [RESET_THREAD_FILTERS] (state) {
    state.filters = initialFiltersState()
  },

  REMOVE_FILE (state, { fileId }) {
    state.thread.messages = state.thread.messages.map(message => {
      if (message.file && message.file.id === fileId) {
        message.file = null
        message.deleted_at = moment()
      }

      return message
    })
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
